import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Plus, Trash2, ExternalLink, Check, Circle, Ban, Undo2, RefreshCw, Loader2, AlertCircle } from "lucide-react";
import InfoBentoCard from "@ui/InfoBentoCard";
import ReasonPrompt from "@ui/ReasonPrompt";
import { useShoppingList } from "@data/hooks/useShoppingList";
import { isDropped, isTaxable, itemCost, lineSubtotal, newShoppingItem } from "@domain/shopping/shoppingList";
import usePriceLookup, { looksLikeUrl, normalizeUrl } from "../hooks/usePriceLookup";

const money = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const inputStyle = {
  background: "var(--bg-input)",
  border: "1px solid var(--border-input)",
};

const cellInput =
  "bg-transparent border border-transparent rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500 hover:border-gray-600";

const NO_CATEGORY = "none";

function StatusToggle({ status, onToggle }) {
  const purchased = status === "purchased";
  return (
    <button
      onClick={onToggle}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap transition-colors ${
        purchased
          ? "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"
          : "bg-amber-500/15 text-amber-400 hover:bg-amber-500/25"
      }`}
      title="Click to toggle"
    >
      {purchased ? <Check className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
      {purchased ? "Purchased" : "Outstanding"}
    </button>
  );
}

// Commit (blur / Enter) is what triggers a price lookup, not every keystroke.
function LinkCell({ link, onChange, onCommit, onRefresh, pending }) {
  return (
    <div className="flex items-center gap-1">
      <input
        type="url"
        value={link || ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onCommit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        placeholder="Paste link"
        className={`w-full min-w-0 text-[11px] text-gray-400 placeholder-gray-600 ${cellInput}`}
      />
      {link && (
        <>
          <button
            onClick={onRefresh}
            disabled={pending}
            className="flex-shrink-0 text-gray-500 hover:text-indigo-300 disabled:opacity-60"
            title="Fetch the current price from this link"
            aria-label="Fetch price"
          >
            {pending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
          <a
            href={normalizeUrl(link)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 text-indigo-400 hover:text-indigo-300"
            title="Open link"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </>
      )}
    </div>
  );
}

const SOURCE_LABEL = {
  page: "from store",
  "page-ai": "AI read page",
  search: "AI search · verify",
};

/**
 * Icon beside the price: spinner while fetching, ! on error, ✦ once a price
 * was looked up. Hover (or tap) shows the details. The tooltip opens to the
 * left so the table's scroll container doesn't clip it; visibility is used
 * instead of `hidden` because the CAD viewer injects a global `.hidden`.
 */
function PriceBadge({ item, pending, error }) {
  const pl = item.priceLookup;
  const current = pl && pl.url === normalizeUrl(item.link);
  let icon;
  let tone;
  let text;
  if (pending) {
    icon = <Loader2 className="w-3 h-3 animate-spin" />;
    tone = "text-indigo-300";
    text = "Fetching price…";
  } else if (error) {
    icon = <AlertCircle className="w-3 h-3" />;
    tone = "text-red-400";
    text = error;
  } else if (current) {
    icon = <span className="text-[11px] leading-none">✦</span>;
    tone = pl.source === "search" || pl.confidence === "low" ? "text-amber-400" : "text-gray-500";
    const date = new Date(pl.fetchedAt).toLocaleDateString(undefined, { month: "numeric", day: "numeric" });
    text = `${SOURCE_LABEL[pl.source]} · ${date}${pl.note ? ` — ${pl.note}` : ""}`;
  } else {
    return <span className="w-3 flex-shrink-0" />;
  }
  return (
    <button type="button" className={`group relative w-3 flex-shrink-0 flex items-center justify-center ${tone}`} aria-label={text}>
      {icon}
      <span
        className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus:visible group-focus:opacity-100 transition-opacity absolute right-full top-1/2 -translate-y-1/2 mr-2 z-50 w-56 px-2.5 py-1.5 rounded-lg text-[10px] text-left font-sans whitespace-normal text-gray-300 shadow-lg pointer-events-none"
        style={{ background: "var(--bg-card-frosted)", border: "1px solid var(--border-card-frosted)" }}
      >
        {text}
      </span>
    </button>
  );
}

function NumberInput({ value, onChange, className = "", ...rest }) {
  return (
    <input
      type="number"
      min="0"
      value={value || ""}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      placeholder="0"
      className={`text-right text-xs font-mono text-gray-300 ${cellInput} ${className}`}
      {...rest}
    />
  );
}

export default function ShoppingList({ budget }) {
  const { items, setItems, loaded, taxRate, setTaxRate, totals } = useShoppingList();
  const [filter, setFilter] = useState("all");
  const [droppingId, setDroppingId] = useState(null);
  const [quickName, setQuickName] = useState("");
  const [quickCat, setQuickCat] = useState("");

  const categories = useMemo(
    () => (budget || []).map((c) => ({ id: c.id, name: c.name })),
    [budget]
  );

  const active = items.filter((i) => !isDropped(i));
  const dropped = items.filter(isDropped).sort((a, b) => (b.droppedAt || 0) - (a.droppedAt || 0));
  const purchasedCount = active.filter((i) => i.status === "purchased").length;

  const visible = active.filter((i) => {
    if (filter === "outstanding") return i.status !== "purchased";
    if (filter === "purchased") return i.status === "purchased";
    return true;
  });

  // Group by Plan line, in Plan order; items not tied to a line go last.
  const groups = useMemo(() => {
    const byId = new Map();
    for (const item of visible) {
      const known = categories.some((c) => c.id === item.categoryId);
      const key = known ? item.categoryId : NO_CATEGORY;
      if (!byId.has(key)) byId.set(key, []);
      byId.get(key).push(item);
    }
    const ordered = categories
      .filter((c) => byId.has(c.id))
      .map((c) => ({ key: c.id, name: c.name, items: byId.get(c.id) }));
    if (byId.has(NO_CATEGORY)) {
      ordered.push({ key: NO_CATEGORY, name: "Not in the Plan", items: byId.get(NO_CATEGORY) });
    }
    return ordered;
  }, [visible, categories]);

  const update = useCallback(
    (id, patch) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i))),
    [setItems]
  );
  const { lookup, pending, errors } = usePriceLookup(update);

  // On load, fetch once for items saved with a link but no price and no
  // prior lookup. Runs only for the initial snapshot — links typed or pasted
  // afterwards are handled by onLinkCommit, so a paste doesn't fire twice.
  const initialSweepDone = useRef(false);
  useEffect(() => {
    if (!loaded || initialSweepDone.current) return;
    initialSweepDone.current = true;
    for (const item of items) {
      if (isDropped(item) || item.unitPrice || item.priceLookup || !looksLikeUrl(item.link)) continue;
      lookup(item);
    }
  }, [loaded, items, lookup]);

  // Link committed: look up if it's new (or was never looked up).
  const onLinkCommit = (item) => {
    if (!looksLikeUrl(item.link)) return;
    if (item.priceLookup?.url === normalizeUrl(item.link)) return;
    lookup(item, { overwrite: Boolean(item.priceLookup) });
  };

  const toggleStatus = (item) =>
    update(item.id, { status: item.status === "purchased" ? "outstanding" : "purchased" });

  const drop = (id, reason) => {
    update(id, { droppedAt: Date.now(), droppedReason: reason });
    setDroppingId(null);
  };

  const restore = (id) => update(id, { droppedAt: null });

  const deleteForever = (item) => {
    if (window.confirm(`Permanently delete "${item.name || "this item"}"? Its drop note will be lost.`)) {
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    }
  };

  const quickAdd = () => {
    const name = quickName.trim();
    if (!name) return;
    setItems((prev) => [
      ...prev,
      newShoppingItem({ name, categoryId: quickCat === "" ? null : Number(quickCat) }),
    ]);
    setQuickName("");
  };

  const categorySelect = (item, className = "") => (
    <select
      value={item.categoryId ?? ""}
      onChange={(e) => update(item.id, { categoryId: e.target.value === "" ? null : Number(e.target.value) })}
      className={`text-[10px] text-gray-500 bg-transparent outline-none cursor-pointer max-w-full truncate ${className}`}
      title="Plan line"
    >
      <option value="">Not in the Plan</option>
      {categories.map((c) => (
        <option key={c.id} value={c.id}>{c.name}</option>
      ))}
    </select>
  );

  // Drop = decided not to buy (kept with a reason); Remove = added by mistake.
  const removeItem = (item) => {
    if (window.confirm(`Remove "${item.name || "this item"}"? (Use Drop instead if you want to record why you're not buying it.)`)) {
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    }
  };

  const dropButton = (item) => (
    <div className="flex items-center gap-1.5">
      <button
        onClick={() => setDroppingId(item.id)}
        className="text-gray-600 hover:text-amber-400"
        title="Drop: decided not to buy (you'll be asked why)"
        aria-label="Drop item"
      >
        <Ban className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => removeItem(item)}
        className="text-gray-600 hover:text-red-400"
        title="Remove: added by mistake"
        aria-label="Remove item"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );

  const taxToggle = (item) =>
    taxRate > 0 && (
      <button
        onClick={() => update(item.id, { taxable: !isTaxable(item) })}
        className={`text-[10px] px-1.5 py-0.5 rounded font-semibold font-sans ${
          isTaxable(item) ? "bg-blue-500/10 text-blue-300" : "bg-gray-500/15 text-gray-500 line-through"
        }`}
        title={isTaxable(item) ? `${taxRate}% sales tax applied — click if this isn't taxed` : "No sales tax — click to apply it"}
      >
        tax
      </button>
    );

  const dropPrompt = (item) => (
    <ReasonPrompt
      placeholder={`Why don't you need "${item.name || "this"}"?`}
      confirmLabel="Drop"
      onConfirm={(reason) => drop(item.id, reason)}
      onCancel={() => setDroppingId(null)}
    />
  );

  const quickAddRow = (
    <div className="flex items-center gap-2">
      <Plus className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
      <input
        type="text"
        value={quickName}
        onChange={(e) => setQuickName(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") quickAdd(); }}
        placeholder="Add an item… press Enter"
        className="flex-1 min-w-0 text-xs bg-transparent focus:outline-none text-gray-300 placeholder-gray-500"
      />
      <select
        value={quickCat}
        onChange={(e) => setQuickCat(e.target.value)}
        className="text-[11px] text-gray-400 bg-transparent outline-none cursor-pointer max-w-[45%] truncate"
        aria-label="Plan line for new item"
      >
        <option value="">Not in the Plan</option>
        {categories.map((c) => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
    </div>
  );

  const filters = [
    { key: "all", label: `All (${active.length})` },
    { key: "outstanding", label: `Outstanding (${active.length - purchasedCount})` },
    { key: "purchased", label: `Purchased (${purchasedCount})` },
    { key: "dropped", label: `Dropped (${dropped.length})` },
  ];

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto pb-24 md:pb-6">
      {/* Header */}
      <InfoBentoCard
        cornerGlow
        icon={<ShoppingCart className="w-4 h-4" />}
        accent="emerald"
        title="Shopping List"
        subtitle="Materials you're buying yourself, itemized from your Plan"
        className="mb-4"
      />

      {/* Totals */}
      <div className="grid grid-cols-3 gap-2 md:gap-3 mb-4">
        <InfoBentoCard accent="blue">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">Planned</p>
          <p className="text-sm md:text-lg font-mono font-semibold text-gray-200">{money(totals.planned)}</p>
          <p className="hidden md:block text-[10px] font-mono text-gray-500">
            {money(totals.subtotal)} + {money(totals.tax)} tax + {money(totals.shipping)} ship
          </p>
        </InfoBentoCard>
        <InfoBentoCard accent="emerald">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">Purchased</p>
          <p className="text-sm md:text-lg font-mono font-semibold text-emerald-400">{money(totals.purchased)}</p>
        </InfoBentoCard>
        <InfoBentoCard accent="amber">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">Outstanding</p>
          <p className="text-sm md:text-lg font-mono font-semibold text-amber-400">{money(totals.outstanding)}</p>
        </InfoBentoCard>
      </div>

      {/* Filters + tax rate */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
              filter === f.key ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-gray-200"
            }`}
            style={filter === f.key ? undefined : inputStyle}
          >
            {f.label}
          </button>
        ))}
        <label className="ml-auto flex items-center gap-1.5 text-[11px] text-gray-400" title="Applied to every item's price × qty (not shipping)">
          Sales tax
          <input
            type="number"
            min="0"
            step="0.001"
            value={taxRate || ""}
            onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
            placeholder="0"
            className="w-16 text-right text-xs font-mono text-gray-200 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500"
            style={inputStyle}
          />
          %
        </label>
      </div>

      {loaded && items.length === 0 && (
        <InfoBentoCard accent="emerald" className="text-center py-10 mb-4">
          <ShoppingCart className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-sm font-semibold mb-1" style={{ color: "var(--text-heading)" }}>
            Nothing on your list yet
          </p>
          <p className="text-xs max-w-md mx-auto" style={{ color: "var(--text-secondary)" }}>
            Open a line on the <Link to="/budget" className="text-indigo-400 hover:text-indigo-300">Plan</Link> and
            list the materials you're buying yourself (appliances, tile, fixtures, outlets). They'll show up here
            to track links, prices, and purchases. Lines with prices in their notes can be itemized in one click.
          </p>
          <div className="max-w-md mx-auto mt-4 px-3 py-2 rounded-lg text-left" style={inputStyle}>{quickAddRow}</div>
        </InfoBentoCard>
      )}

      {/* ── Dropped ── */}
      {filter === "dropped" && (
        <InfoBentoCard cornerGlow frostedGradient accent="rose">
          {dropped.length === 0 ? (
            <p className="text-xs text-gray-500 text-center py-4">Nothing dropped yet.</p>
          ) : (
            <div className="-mx-4 -mb-4">
              {dropped.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3 px-4 py-2.5"
                  style={{ borderBottom: "1px solid var(--border-row)" }}
                >
                  <div className="md:w-64 min-w-0">
                    <p className="text-xs text-gray-300 line-through truncate">{item.name || "Untitled"}</p>
                    <p className="text-[10px] text-gray-500 truncate">
                      {categories.find((c) => c.id === item.categoryId)?.name || "Not in the Plan"} · {money(itemCost(item, taxRate))}
                    </p>
                  </div>
                  <input
                    type="text"
                    value={item.droppedReason || ""}
                    onChange={(e) => update(item.id, { droppedReason: e.target.value })}
                    placeholder="Why it's not needed…"
                    className={`flex-1 min-w-0 text-[11px] text-gray-300 italic placeholder-gray-600 ${cellInput}`}
                  />
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-[10px] text-gray-500">
                      {new Date(item.droppedAt).toLocaleDateString()}
                    </span>
                    <button onClick={() => restore(item.id)} className="flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-300">
                      <Undo2 className="w-3 h-3" /> Restore
                    </button>
                    <button onClick={() => deleteForever(item)} className="text-gray-600 hover:text-red-400" aria-label="Delete permanently" title="Delete permanently">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </InfoBentoCard>
      )}

      {/* ── Desktop table ── */}
      {filter !== "dropped" && items.length > 0 && (
        <InfoBentoCard cornerGlow frostedGradient accent="emerald" className="hidden md:block">
          <div className="overflow-x-auto -mx-4">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border-row)" }}>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Item</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold w-44">Plan line</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold w-44">Link</th>
                  <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Price</th>
                  <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Qty</th>
                  <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Shipping</th>
                  <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold" title="Price × qty + tax + shipping">Total</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Status</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <React.Fragment key={g.key}>
                    <tr style={{ background: "var(--bg-row-hover)", borderBottom: "1px solid var(--border-row)" }}>
                      <td colSpan={6} className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-blue-300">
                        {g.name}
                      </td>
                      <td className="px-3 py-1.5 text-right text-[11px] font-mono font-semibold text-blue-300">
                        {money(g.items.reduce((s, i) => s + itemCost(i, taxRate), 0))}
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                    {g.items.map((item) =>
                      droppingId === item.id ? (
                        <tr key={item.id} style={{ borderBottom: "1px solid var(--border-row)" }}>
                          <td colSpan={9} className="px-3 py-2">{dropPrompt(item)}</td>
                        </tr>
                      ) : (
                        <tr key={item.id} className="group" style={{ borderBottom: "1px solid var(--border-row)" }}>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={item.name}
                              onChange={(e) => update(item.id, { name: e.target.value })}
                              placeholder="Item name"
                              className={`w-full text-xs text-gray-200 placeholder-gray-600 ${cellInput}`}
                            />
                          </td>
                          <td className="px-3 py-2">{categorySelect(item, "w-full")}</td>
                          <td className="px-3 py-2">
                            <LinkCell
                              link={item.link}
                              onChange={(link) => update(item.id, { link })}
                              onCommit={() => onLinkCommit(item)}
                              onRefresh={() => lookup(item, { overwrite: true })}
                              pending={pending[item.id]}
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <NumberInput value={item.unitPrice} step="0.01" onChange={(v) => update(item.id, { unitPrice: v })} className="w-24" />
                              <PriceBadge item={item} pending={pending[item.id]} error={errors[item.id]} />
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <NumberInput value={item.quantity} step="1" onChange={(v) => update(item.id, { quantity: v })} className="w-14" />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <NumberInput value={item.shipping} step="0.01" onChange={(v) => update(item.id, { shipping: v })} className="w-20" />
                          </td>
                          <td
                            className="px-3 py-2 text-right text-xs font-mono font-semibold text-gray-200 whitespace-nowrap"
                            title={`${money(lineSubtotal(item))}${isTaxable(item) ? ` + ${taxRate}% tax` : " (no tax)"} + ${money(item.shipping)} shipping`}
                          >
                            <div className="flex items-center justify-end gap-1.5">
                              {taxToggle(item)}
                              {money(itemCost(item, taxRate))}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <StatusToggle status={item.status} onToggle={() => toggleStatus(item)} />
                          </td>
                          <td className="px-2 opacity-0 group-hover:opacity-100 transition-opacity">{dropButton(item)}</td>
                        </tr>
                      )
                    )}
                  </React.Fragment>
                ))}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-6 text-center text-xs text-gray-500">No items match this filter.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="pt-3 mt-1">{quickAddRow}</div>
        </InfoBentoCard>
      )}

      {/* ── Mobile cards ── */}
      {filter !== "dropped" && (
        <div className="md:hidden space-y-3">
          {groups.map((g) => (
            <div key={g.key} className="space-y-2">
              <div className="flex items-center justify-between px-1">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-blue-300">{g.name}</p>
                <p className="text-[11px] font-mono font-semibold text-blue-300">
                  {money(g.items.reduce((s, i) => s + itemCost(i, taxRate), 0))}
                </p>
              </div>
              {g.items.map((item) => (
                <InfoBentoCard key={item.id} accent={item.status === "purchased" ? "emerald" : "amber"}>
                  {droppingId === item.id ? (
                    dropPrompt(item)
                  ) : (
                    <>
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <input
                            type="text"
                            value={item.name}
                            onChange={(e) => update(item.id, { name: e.target.value })}
                            placeholder="Item name"
                            className={`w-full text-sm text-gray-200 placeholder-gray-600 ${cellInput}`}
                          />
                          {categorySelect(item, "ml-1")}
                        </div>
                        <div className="p-1">{dropButton(item)}</div>
                      </div>
                      <div className="mt-2">
                        <LinkCell
                              link={item.link}
                              onChange={(link) => update(item.id, { link })}
                              onCommit={() => onLinkCommit(item)}
                              onRefresh={() => lookup(item, { overwrite: true })}
                              pending={pending[item.id]}
                            />
                      </div>
                      <div className="flex items-center gap-1.5 mt-2 text-[10px] text-gray-500">
                        <NumberInput value={item.unitPrice} step="0.01" inputMode="decimal" onChange={(v) => update(item.id, { unitPrice: v })} className="w-20" style={inputStyle} />
                        <PriceBadge item={item} pending={pending[item.id]} error={errors[item.id]} />
                        ×
                        <NumberInput value={item.quantity} step="1" inputMode="numeric" onChange={(v) => update(item.id, { quantity: v })} className="w-12" style={inputStyle} />
                        + ship
                        <NumberInput value={item.shipping} step="0.01" inputMode="decimal" onChange={(v) => update(item.id, { shipping: v })} className="w-16" style={inputStyle} />
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <StatusToggle status={item.status} onToggle={() => toggleStatus(item)} />
                        <span className="flex items-center gap-1.5 text-sm font-mono font-semibold text-gray-200">
                          {taxToggle(item)}
                          {money(itemCost(item, taxRate))}
                        </span>
                      </div>
                    </>
                  )}
                </InfoBentoCard>
              ))}
            </div>
          ))}
          {items.length > 0 && (
            <div className="px-3 py-2 rounded-lg" style={inputStyle}>{quickAddRow}</div>
          )}
        </div>
      )}
    </div>
  );
}
